import { BuiltInAttribute, type ShortTaskOutput } from "@orsane/schema/tasks";
import { SquareArrowOutUpRight } from "lucide-react";
import { memo, useCallback } from "react";
import CellButton from "@/components/CellButton";
import { TaskSelect } from "@/components/property-selectors/TaskSelect";
import { useTaskNavigation } from "@/hooks/useTaskNavigation";
import { useChangeTaskParentMutation } from "@/mutations/task-mutations";
import type { ColumnDef } from "../../ColumnModel";
import type { Row } from "../../RowModel";

// Static function - doesn't need to be recreated

const getSortAndGroupValue = ({ row }: { row: Row }) => {
    return `${row.data.parentTask?.id}___${JSON.stringify({ number: row.data.parentTask?.number, title: row.data.parentTask?.title })}`;
};

// Static function - doesn't need to be recreated
const getGroupHeader = ({ row }: { row: Row }) => {
    if (!row.data.parentTask) {
        return "None";
    }
    return `T-${row.data.parentTask?.number} ${row.data.parentTask?.title}`;
};

// Memoized cell component
const TaskSelectCell = memo(function TaskSelectCell({
    row,
    onValueChange,
}: {
    row: Row;
    onValueChange: (taskId: number, value: ShortTaskOutput | null) => void;
}) {
    const { openTask: navigateToTask } = useTaskNavigation();
    const handleChange = useCallback(
        (value: ShortTaskOutput | null) => {
            onValueChange(row.data.id, value);
        },
        [row.data.id, onValueChange]
    );

    const _handleOpenParentTask = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (row.data.parentTask) {
                navigateToTask(row.data.parentTask.id);
            }
        },
        [row.data.parentTask, navigateToTask]
    );

    return (
        <TaskSelect
            ignoreId={row.data.id}
            trigger={(content, _value) => (
                <CellButton disabled={row.data.isNewlyCreated} className="justify-between group/parenttask">
                    {content}
                    {/* {row.data.parentTask && (
                        <button
                            type="button"
                            className="size-6 place-items-center group-hover/parenttask:grid hidden cursor-pointer text-orsane-muted hover:text-orsane-primary appearance-none p-1 pr-0"
                            onClick={handleOpenParentTask}
                        >
                            <SquareArrowOutUpRight className="size-3.5" />
                        </button>
                    )} */}
                </CellButton>
            )}
            value={row.data.parentTask}
            onChange={handleChange}
        />
    );
});

export default function useParentTaskColumnBuilder() {
    const { mutate } = useChangeTaskParentMutation();

    // Stable mutation handler
    const handleValueChange = useCallback(
        (taskId: number, value: ShortTaskOutput | null) => {
            mutate({
                id: taskId,
                parentTask: value,
            });
        },
        [mutate]
    );

    return {
        build: useCallback(
            (isVisible: boolean): ColumnDef => {
                return {
                    id: BuiltInAttribute.parentTask,
                    header: "Parent task",
                    isVisible: isVisible,
                    cssColDef: "200px",
                    isRight: false,
                    isReadOnly: false,
                    getSortValue: getSortAndGroupValue,
                    getGroupHeader: getGroupHeader,
                    cell: ({ row }: { row: Row }) => <TaskSelectCell row={row} onValueChange={handleValueChange} />,
                };
            },
            [handleValueChange]
        ),
    };
}
